// ============================================
// startMixedRecording — AudioContext mixer + MediaRecorder
// ============================================
// Wave 5e2 — extracted from initializeWebRTC().ontrack handler.
//
// Pipeline (when both mic + bot streams are available):
//   1. Create a fresh AudioContext, resume if suspended (mobile browsers).
//   2. Create MediaStreamSource for mic AND bot remote stream.
//   3. Pipe both into a MediaStreamDestination — this is the mixed output.
//   4. Wrap that mixed stream in a MediaRecorder (audio/webm;codecs=opus),
//      capturing 1-second chunks. ondataavailable buffers locally AND, if
//      progressive chunk upload is active, into chunkBufferRef which the
//      caller flushes periodically.
//
// Fallback (when AudioContext / mixed-stream setup throws):
//   - Record the user's mic stream alone with the same MediaRecorder
//     options. Bot voice will be missing from the saved recording, but
//     the call still proceeds normally.
//
// Refs mutated (matches inline behavior exactly):
//   - audioContextRef.current   = AudioContext
//   - mixedStreamRef.current    = destination.stream
//   - mediaRecorderRef.current  = MediaRecorder
//   - audioChunksRef.current    = []   (reset before recording)
//   - recordedAudioBlobRef.current set on MediaRecorder.onstop
//   - chunkBufferRef + chunkUploadPromisesRef written from ondataavailable

import type { MutableRefObject } from 'react'

export interface StartMixedRecordingDeps {
  audioContextRef: MutableRefObject<AudioContext | null>
  mixedStreamRef: MutableRefObject<MediaStream | null>
  mediaRecorderRef: MutableRefObject<MediaRecorder | null>
  audioChunksRef: MutableRefObject<Blob[]>
  recordedAudioBlobRef: MutableRefObject<Blob | null>
  chunkUploadActiveRef: MutableRefObject<boolean>
  chunkBufferRef: MutableRefObject<Blob[]>
  chunkUploadPromisesRef: MutableRefObject<Promise<boolean>[]>
  flushChunkBuffer: () => Promise<boolean>
  chunkFlushInterval: number
}

export const startMixedRecording = (
  micStream: MediaStream,
  remoteStream: MediaStream,
  deps: StartMixedRecordingDeps,
): void => {
  const {
    audioContextRef,
    mixedStreamRef,
    mediaRecorderRef,
    audioChunksRef,
    recordedAudioBlobRef,
    chunkUploadActiveRef,
    chunkBufferRef,
    chunkUploadPromisesRef,
    flushChunkBuffer,
    chunkFlushInterval,
  } = deps

  try {
    console.log('🎙️ Setting up mixed audio recording (user + bot)...')

    // Create AudioContext for mixing
    const audioContext = new AudioContext()
    audioContextRef.current = audioContext

    // Resume AudioContext if suspended (required on mobile browsers like Android Chrome)
    if (audioContext.state === 'suspended') {
      audioContext
        .resume()
        .then(() => {
          console.log(
            '🔊 AudioContext resumed from suspended state (mobile browser)',
          )
        })
        .catch((err: unknown) => {
          console.warn('⚠️ Failed to resume AudioContext:', err)
        })
    }

    // Create sources
    const micSource = audioContext.createMediaStreamSource(micStream)
    const botSource = audioContext.createMediaStreamSource(remoteStream)

    // Create destination for mixed audio
    const destination = audioContext.createMediaStreamDestination()

    // Connect both sources to destination (this mixes them)
    micSource.connect(destination)
    botSource.connect(destination)

    // Store mixed stream
    mixedStreamRef.current = destination.stream

    // Create MediaRecorder with mixed stream
    const mediaRecorder = new MediaRecorder(destination.stream, {
      mimeType: 'audio/webm;codecs=opus',
    })
    mediaRecorderRef.current = mediaRecorder
    audioChunksRef.current = []

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data)
        // Also buffer for progressive chunk upload
        if (chunkUploadActiveRef.current) {
          chunkBufferRef.current.push(event.data)
          if (chunkBufferRef.current.length >= chunkFlushInterval) {
            const promise = flushChunkBuffer()
            chunkUploadPromisesRef.current.push(promise)
          }
        }
      }
    }

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunksRef.current, {
        type: 'audio/webm',
      })
      recordedAudioBlobRef.current = audioBlob
      console.log(
        '🎙️ Mixed recording saved (user + bot):',
        audioBlob.size,
        'bytes',
      )
      // AudioContext cleanup is handled in endSession after MediaRecorder finalization
    }

    mediaRecorder.start(1000) // Capture in 1-second chunks
    console.log('✅ Started mixed recording (user microphone + bot voice)')
  } catch (err) {
    console.warn(
      '⚠️ Failed to start mixed recording, falling back to user-only:',
      err,
    )

    // Fallback: Record user audio only (original behavior)
    try {
      const mediaRecorder = new MediaRecorder(micStream, {
        mimeType: 'audio/webm;codecs=opus',
      })
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
          // Also buffer for progressive chunk upload
          if (chunkUploadActiveRef.current) {
            chunkBufferRef.current.push(event.data)
            if (chunkBufferRef.current.length >= chunkFlushInterval) {
              const promise = flushChunkBuffer()
              chunkUploadPromisesRef.current.push(promise)
            }
          }
        }
      }

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/webm',
        })
        recordedAudioBlobRef.current = audioBlob
        console.log(
          '🎙️ Recording saved (user only - fallback):',
          audioBlob.size,
          'bytes',
        )
      }

      mediaRecorder.start(1000)
      console.log('🎙️ Started fallback recording (user audio only)')
    } catch (fallbackErr) {
      console.error('❌ Failed to start any recording:', fallbackErr)
    }
  }
}
